import moment from "moment";
import { getDayTime, isFutureDate } from "./dates";
import { needsApproval } from "./helpers";

// Status palette. Source of truth is the --cc-status-* / RAG block in
// design-system/carecentre/tokens.css — these literals mirror it for JS
// consumers (MUI inline styles, SVG fills) that cannot read a CSS variable.
// Keep the two in step: change one, change the other.
//
// Green / amber / red are the SAME values the budget cell uses, so a shift and
// a budget can never disagree about what green means. The remaining hues are
// identities rather than severities — they answer "which kind of thing is this",
// so they are deliberately held apart from R/A/G and from one another.
export const status = {
  ok: `#059669`,
  okSoft: `#e8f5ec`,
  warn: `#d97706`,
  warnSoft: `#fef3e6`,
  danger: `#dc2626`,
  dangerSoft: `#fdecea`,
  late: `#db2777`,
  lateSoft: `#fce7f3`,
  info: `#2563eb`,
  infoSoft: `#e7efff`,
  muted: `#6b7482`,
  mutedSoft: `#eef1f5`,
  deleted: `#7c3aed`,
  deletedSoft: `#f1ebfe`,
  remote: `#0e7490`,
  remoteSoft: `#e0f2fe`,
};

// The hue-named keys below are the legacy vocabulary, still read by ~37 files.
// They now resolve to the palette above, so recolouring happens in one place.
// New code should prefer the semantic `status` names.
export let colors = {
  // late clock-in / early clock-out
  pinkBG: status.lateSoft,
  pinkBorder: status.late,

  // action required
  amberBG: status.warnSoft,
  amberBorder: status.warn,

  redBG: status.dangerSoft,
  redBorder: status.danger,

  // clock in/out updated
  blueBG: status.infoSoft,
  blueBorder: status.info,

  // clocked in/out on time
  greenBG: status.okSoft,
  greenBorder: status.ok,

  // dropped / day off called
  greyBG: status.mutedSoft,
  greyText: status.muted,

  // deleted shift
  magentaBG: status.deletedSoft,
  magentaBorder: status.deleted,

  // remote work
  purpleBG: status.remoteSoft,
  purpleBorder: status.remote,

  primary: `#2563eb`,
  primaryLight: "#42a5f5",

  primaryLightBG: "#f8fafc",

  amberDarkBG: "#fffcf4",
  amberDarkBorder: "#c69701",
};

export const leaveColors = {
  holiday: `#42a5f5`,
  sickness: `#e57373`,
  public: `#5c6bc0`,
  authorized: `#81c784`,
  unauthorized: `#ffeaea`,
  compassionate: `#ba68c8`,
  maternity: `#f8bbd0`,
  paternity: `#f8bbd0`,
  parental: `#ce93d8`,
  study: `#aed581`,
  training: `#aed581`,
  furlough: `#bcaaa4`,
  other: `#80cbc4`,
  rejected: `#e57373`,
  pending: `#fff8e1`,
};
export const leaveBg = {
  holiday: `repeating-linear-gradient(-55deg,#42a5f5,#42a5f5 4px,#fff 4px,#fff 10px)`,
  sickness: `repeating-linear-gradient(-55deg,#e57373,#e57373 4px,#fff 4px,#fff 10px)`,
  public: `repeating-linear-gradient(-55deg,#5c6bc0,#5c6bc0 4px,#fff 4px,#fff 10px)`,
  authorized: `repeating-linear-gradient(-55deg,#81c784,#81c784 4px,#fff 4px,#fff 10px)`,
  unauthorized: `repeating-linear-gradient(-55deg,#ffeaea,#ffeaea 4px,#fff 4px,#fff 10px)`,
  compassionate: `repeating-linear-gradient(-55deg,#ba68c8,#ba68c8 4px,#fff 4px,#fff 10px)`,
  maternity: `repeating-linear-gradient(-55deg,#f8bbd0,#f8bbd0 4px,#fff 4px,#fff 10px)`,
  paternity: `repeating-linear-gradient(-55deg,#f8bbd0,#f8bbd0 4px,#fff 4px,#fff 10px)`,
  parental: `repeating-linear-gradient(-55deg,#ce93d8,#ce93d8 4px,#fff 4px,#fff 10px)`,
  study: `repeating-linear-gradient(-55deg,#aed581,#aed581 4px,#fff 4px,#fff 10px)`,
  training: `repeating-linear-gradient(-55deg,#aed581,#aed581 4px,#fff 4px,#fff 10px)`,
  furlough: `repeating-linear-gradient(-55deg,#bcaaa4,#bcaaa4 4px,#fff 4px,#fff 10px)`,
  other: `repeating-linear-gradient(-55deg,#80cbc4,#80cbc4 4px,#fff 4px,#fff 10px)`,
  pending: `repeating-linear-gradient(-55deg, #ffd54f, #ffd54f 4px, #fff 4px, #fff 10px)`,
};

let defaultShiftCardStyle = {
  borderLeft: "4px solid #0a0a29",
  display: "flex",
  backgroundColor: "#fff",
  padding: `6px`,
  cursor: "pointer",
  "&:not(:last-child)": {
    marginBottom: "10px",
  },
  position: "relative",
  width: "100%",
  // height: "100%",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",
};

export const setShiftCardStyle = (shift, settings) => {
  let { swap_shift: swap } = shift;
  let styles = { ...defaultShiftCardStyle };

  let isDayOff = shift?.day_off_called || shift?.day_off_called === 1;
  let isDropped = shift.is_shift_drop_approved;
  let isClocked =
    shift?.clock_in_out_details?.id !== undefined &&
    shift?.clock_in_out_details?.id !== null;

  let current = getDayTime();
  let shiftStartTime = `${shift?.shift_date} ${shift.shift_start_time}`; // 2024-07-08 18:10:00
  let shiftEndTime = `${shift?.shift_date} ${shift.shift_end_time}`; // 2024-07-08 18:10:00

  let isFuture = isFutureDate(current, shiftStartTime);
  let hasEndPassed = isFutureDate(current, shiftEndTime);
  let isEndGreater = moment(shiftStartTime, "YYYY-MM-DD HH:mm:ss").isAfter(
    shiftEndTime,
    "YYYY-MM-DD HH:mm:ss",
  );

  if (isEndGreater) {
    shiftEndTime = moment(shiftEndTime, "YYYY-MM-DD HH:mm:ss")
      .add(1, "days")
      .format("YYYY-MM-DD HH:mm:ss");
    isEndGreater = moment(shiftStartTime, "YYYY-MM-DD HH:mm:ss").isAfter(
      shiftEndTime,
      "YYYY-MM-DD HH:mm:ss",
    );
    hasEndPassed = isFutureDate(current, shiftEndTime);
  }

  // not published
  if (!shift.is_published) {
    styles.borderColor = "grey";
  }

  // published
  if (shift?.is_published) {
    // swap request
    if (swap?.id) {
      // needs approval
      if (
        needsApproval(settings) &&
        swap?.request_actioned_on !== null &&
        swap?.is_request_accepted
      ) {
        if (swap?.shift_swap_approved_on == null) {
          styles.borderColor = colors.amberBorder;
          styles.backgroundColor = colors.amberBG;
        }
      }
    }

    // drop request pending
    if (
      shift?.is_shift_drop_requested &&
      shift?.shift_drop_approved_by == null
    ) {
      styles.borderColor = colors.amberBorder;
      styles.backgroundColor = colors.amberBG;
    }

    // drop approved
    if (shift?.is_shift_drop_requested && shift.is_shift_drop_approved) {
      styles.borderColor = `#938989`;
      styles.backgroundColor = `#dedede`;
    }

    // leave - day off called without clock
    if (shift?.day_off_called === 1 || shift?.day_off_called === true) {
      // clock in data found
      // if (clockList.find((ele) => ele.shift_id === shift?.id)?.id !== undefined) {
      // } else {
      //   // clock in data not found
      // }
      styles.borderColor = `#938989`;
      styles.backgroundColor = `#dedede`;
    }

    // clock validation
    if (!isDayOff && !isDropped) {
      if (isClocked) {
        // clock data found
        let clock = shift?.clock_in_out_details;

        // created by manager
        if (clock?.is_created_by_manager) {
          styles.borderColor = `#559bf4`;
          styles.backgroundColor = `#cbe1fe`;
        } else {
          // created by employee
          // on time
          if (clock?.is_clock_in_on_time && clock?.is_clock_out_on_time) {
            styles.borderColor = `#70BB63`;
            styles.backgroundColor = `#cbfee1`;
          }

          // late clock in
          if (shift?.is_clocked_in && !clock?.is_clock_in_on_time) {
            styles.borderColor = `#ec7c8f`;
            styles.backgroundColor = `#ffeef0`;
          }

          // late clock out
          if (clock?.clockout_time !== null && !clock?.is_clock_out_on_time) {
            styles.borderColor = `#ec7c8f`;
            styles.backgroundColor = `#ffeef0`;
          }

          // no clock out
          if (shift?.is_clocked_in && !shift?.is_clocked_out) {
            // start today ends tomorrow
            if (isEndGreater) {
              if (isFuture) {
                styles.borderColor = colors.amberBorder;
                styles.backgroundColor = colors.amberBG;
              }
            } else {
              // normal flow of time
              if (hasEndPassed) {
                styles.borderColor = colors.amberBorder;
                styles.backgroundColor = colors.amberBG;
              }
            }
          }
        }
      } else {
        // No clock data found
        if (isFuture && !isClocked) {
          styles.borderColor = colors.amberBorder;
          styles.backgroundColor = colors.amberBG;
        }
      }
    }
  }
  // remote work allowed
  if (shift?.is_remote_work) {
    styles.borderColor = colors.purpleBorder;
    styles.backgroundColor = colors.purpleBG;
  }

  // deleted shift
  if (shift?.deleted_at && shift?.deleted_at !== null) {
    styles.borderColor = colors.magentaBorder;
    styles.backgroundColor = colors.magentaBG;
  }
  return styles;
};

export const setDropIconColor = (shift) => {
  let color = colors.amberBorder;
  if (shift?.shift_drop_approved_by !== null) {
    if (shift?.is_shift_drop_approved) {
      color = colors.greenBorder;
    } else {
      color = colors.redBorder;
    }
  }
  return color;
};

export const setSwapIconColor = (shift) => {
  let color = "#0a0a29";
  let { swap_shift: swap } = shift;
  let needsApproval = swap?.is_swift_swap_approval_required;

  if (swap?.id) {
    if (needsApproval) {
      // actioned by employee
      if (swap?.request_actioned_on !== null) {
        if (swap?.is_request_accepted) color = colors.amberBorder;
        else color = colors.redBorder;

        if (swap?.shift_swap_approved_on !== null) {
          // approved by admin
          if (swap?.is_shift_swap_approved) color = colors.greenBorder;
          // rejected
          else color = colors.redBorder;
        }
      }
    } else {
      // pending
      if (swap?.shift_swap_approved_on === null) {
        color = colors.amberBorder;
      } else {
        // actioned
        // approved
        if (swap?.is_shift_swap_approved) color = colors.greenBorder;
        // rejected
        else color = colors.redBorder;
      }
    }
  }

  return color;
};

export const setClockIconStyle = (shift) => {
  let renderStyle = {
    fontSize: "16px",
    color: "#0a0a29",
  };
  let clock = shift?.clock_in_out_details;
  let isClocked = clock?.id !== undefined;

  let current = getDayTime(); // 2024-07-08 18:00:00

  let shiftStartTime = `${shift?.shift_date} ${shift.shift_start_time}`; // 2024-07-08 18:10:00
  let shiftEndTime = `${shift?.shift_date} ${shift.shift_end_time}`; // 2024-07-08 18:10:00

  let isFuture = isFutureDate(current, shiftStartTime);
  let hasEndPassed = isFutureDate(current, shiftEndTime);
  let isEndGreater = moment(shiftStartTime, "YYYY-MM-DD HH:mm:ss").isAfter(
    shiftEndTime,
    "YYYY-MM-DD HH:mm:ss",
  );

  if (isEndGreater) {
    shiftEndTime = moment(shiftEndTime, "YYYY-MM-DD HH:mm:ss")
      .add(1, "days")
      .format("YYYY-MM-DD HH:mm:ss");
    isEndGreater = moment(shiftStartTime, "YYYY-MM-DD HH:mm:ss").isAfter(
      shiftEndTime,
      "YYYY-MM-DD HH:mm:ss",
    );
    hasEndPassed = isFutureDate(current, shiftEndTime);
  }

  if (clock?.id !== undefined) {
    // created by manager
    if (clock?.is_created_by_manager) {
      renderStyle.color = colors.blueBorder;
    } else {
      // created by employee
      // on time
      if (clock?.is_clock_in_on_time && clock?.is_clock_out_on_time) {
        renderStyle.color = colors.greenBorder;
      }

      // late clock in
      if (shift?.is_clocked_in && !clock?.is_clock_in_on_time) {
        renderStyle.color = colors.pinkBorder;
      }

      // late clock out
      if (clock?.clockout_time !== null && !clock?.is_clock_out_on_time) {
        renderStyle.color = colors.pinkBorder;
      }

      // no clockout
      if (shift?.is_clocked_in && !shift?.is_clocked_out) {
        // start today ends tomorrow
        if (isEndGreater) {
          if (isFuture) {
            renderStyle.color = colors.amberBorder;
          }
        } else {
          // normal flow of time
          if (hasEndPassed) {
            renderStyle.color = colors.amberBorder;
          }
        }
      }
    }
  } else {
    // no clock found
    // no clock data in shift
    if (isFuture && !isClocked) {
      renderStyle.color = colors.amberBorder;
    }
  }

  return renderStyle;
};

export const setClockCardStyle = (clock) => {
  let styles = {};

  // created by manager
  if (clock?.is_created_by_manager) {
    styles.borderColor = `#559bf4`;
    styles.backgroundColor = `#cbe1fe`;
  }
  if (clock?.is_approved) {
    styles.borderColor = `#0a0a29`;
    styles.backgroundColor = `#fff`;
  } else {
    styles.borderColor = `#938989`;
    styles.backgroundColor = `#dedede`;
  }

  // on time
  if (
    clock?.clockin_time &&
    clock?.clockout_time &&
    clock?.is_clock_in_on_time &&
    clock?.is_clock_out_on_time
  ) {
    styles.borderColor = `#70BB63`;
    styles.backgroundColor = `#cbfee1`;
  }

  // late clock out
  if (clock?.clockout_time !== null && !clock?.is_clock_out_on_time) {
    styles.borderColor = `#ec7c8f`;
    styles.backgroundColor = `#ffeef0`;
  }

  // late clock in
  if (clock?.clockin_time !== null && !clock?.is_clock_out_in_time) {
    styles.borderColor = `#ec7c8f`;
    styles.backgroundColor = `#ffeef0`;
  }

  return styles;
};

export const setLeaveCardStyle = (leave) => {
  let styles = {
    backgroundColor: "#2563eb",
  };
  if (leave?.status === "rejected" || leave?.status === "pending") {
    if (leaveColors[leave?.type]) {
      styles.backgroundColor = leaveColors[leave?.type];
    }
    if (leave?.status === "pending") {
      styles.backgroundColor = leaveColors["pending"];
    }
    if (leave?.status === "rejected") {
      styles.backgroundColor = leaveColors["rejected"];
    }
  } else {
    if (leaveColors[leave?.type]) {
      styles.backgroundColor = leaveColors[leave?.type];
    }
  }

  return styles;
};

export const onboardingStatusColors = {
  draft: "#D3D3D3", // Soft Gray
  submitted: "#F7C6D9", // Soft Pink
  "in progress": "#FFE6A7", // Soft Amber/Yellow
  "awaiting hr approval": "#D8C2FF", // Soft Purple
  "request for final review": "#F3C1E5", // Soft Magenta
  "hr approved": "#AED9E0", // Soft Sky Blue
  completed: "#B6EBC6", // Soft Green
  "revisions required": "#C1DAF7", // Light Blue
  skipped: "#FFD1B3", // Soft Orange/Peach
};
